import { useState } from 'react';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Callout } from '@/components/ui/content';
import type { LibraryOpenTarget } from '../artifacts/view-models';
import { LibraryList } from './library-list';
import { useArtifactLibrary } from './use-artifact-library';

// The mounted Artifact Library: ART/SHARE's authorized list through the real API.
// The host decides what opening and "make one" do, so this component imports no
// workspace context; a row opens by its original identity, never by a label.

export interface ArtifactLibraryProps {
  /** Returns false when the host cannot open this exact version; the Library says so. */
  onOpen: (target: LibraryOpenTarget) => boolean | void;
  /** Empty state next step; opens the composer. */
  onMake?: () => void;
}

export function ArtifactLibrary({ onOpen, onMake }: ArtifactLibraryProps) {
  const library = useArtifactLibrary();
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const [openFailed, setOpenFailed] = useState<string | null>(null);
  return (
    <>
      {openFailed !== null && (
        <Callout tone="info">{formatCopy(copy.libraryCannotOpen, { title: openFailed })}</Callout>
      )}
      <LibraryList
        embedded
        vm={library.vm}
        onSegment={(segment) => {
          setOpenFailed(null);
          library.setSegment(segment);
        }}
        onQuery={(query) => {
          setOpenFailed(null);
          library.setQuery(query);
        }}
        onKind={(kind) => {
          setOpenFailed(null);
          library.setKind(kind);
        }}
        filters={library.filters}
        onMore={library.loadMore}
        onRetry={library.retry}
        onMake={onMake}
        onOpen={(id) => {
          const target = library.openTarget(id);
          if (target)
            setOpenFailed(
              onOpen(target) === false
                ? (library.vm.items.find((item) => item.id === id)?.title ?? '')
                : null,
            );
        }}
      />
    </>
  );
}
